/**
 * Colour science for white balance.
 *
 * The decoder hands back linear sRGB that has already been balanced with the
 * camera's as-shot multipliers, so the neutral in the buffer *is* sRGB white.
 * The temperature and tint controls therefore express a re-balance: "assume the
 * scene light was T Kelvin instead of what the camera guessed", which becomes a
 * chromatic adaptation between two illuminants applied in linear sRGB.
 *
 * Doing it this way means the slider behaves the way a photographer expects at
 * both ends of the range — the image warms smoothly and neutrals stay neutral —
 * instead of the naive "scale the red and blue channels" that goes magenta in
 * the shadows and clips the highlights.
 */

export type Matrix3 = [number, number, number, number, number, number, number, number, number];

/** Linear sRGB (Rec.709 primaries, D65) to CIE XYZ. */
export const SRGB_TO_XYZ: Matrix3 = [
  0.4123908, 0.3575843, 0.1804808, 0.212639, 0.7151687, 0.0721923, 0.0193308, 0.1191948, 0.9505322,
];

/** CIE XYZ to linear sRGB. */
export const XYZ_TO_SRGB: Matrix3 = [
  3.2409699, -1.5373832, -0.4986108, -0.9692436, 1.8759675, 0.0415551, 0.0556301, -0.203977,
  1.0569715,
];

/** Bradford cone response, the standard basis for chromatic adaptation. */
const BRADFORD: Matrix3 = [
  0.8951, 0.2664, -0.1614, -0.7502, 1.7135, 0.0367, 0.0389, -0.0685, 1.0296,
];

const BRADFORD_INVERSE: Matrix3 = [
  0.9869929, -0.1470543, 0.1599627, 0.4323053, 0.5183603, 0.0492912, -0.0085287, 0.0400428,
  0.9684867,
];

export function multiplyMatrix(a: Matrix3, b: Matrix3): Matrix3 {
  const out = new Array<number>(9);
  for (let row = 0; row < 3; row += 1) {
    for (let column = 0; column < 3; column += 1) {
      out[row * 3 + column] =
        a[row * 3] * b[column] + a[row * 3 + 1] * b[3 + column] + a[row * 3 + 2] * b[6 + column];
    }
  }
  return out as Matrix3;
}

export function applyMatrix(m: Matrix3, v: [number, number, number]): [number, number, number] {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ];
}

export function invertMatrix(m: Matrix3): Matrix3 | null {
  const [a, b, c, d, e, f, g, h, i] = m;
  const determinant = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  if (Math.abs(determinant) < 1e-12) return null;
  const inverse = 1 / determinant;
  return [
    (e * i - f * h) * inverse,
    (c * h - b * i) * inverse,
    (b * f - c * e) * inverse,
    (f * g - d * i) * inverse,
    (a * i - c * g) * inverse,
    (c * d - a * f) * inverse,
    (d * h - e * g) * inverse,
    (b * g - a * h) * inverse,
    (a * e - b * d) * inverse,
  ];
}

/**
 * Chromaticity of a correlated colour temperature.
 *
 * Below 4000 K the Planckian locus is used (tungsten, candlelight); at and
 * above 4000 K the CIE daylight locus, which is what daylight, shade and flash
 * actually follow. Kim et al.'s cubic approximation of the Planckian locus is
 * accurate to well under a just-noticeable difference across 1667–25000 K.
 */
export function temperatureToChromaticity(kelvin: number): { x: number; y: number } {
  const t = Math.min(25000, Math.max(1667, kelvin));

  if (t < 4000) {
    // Planckian locus.
    const x =
      t <= 4000
        ? -0.2661239e9 / (t * t * t) - 0.2343589e6 / (t * t) + 0.8776956e3 / t + 0.179910
        : -3.0258469e9 / (t * t * t) + 2.1070379e6 / (t * t) + 0.2226347e3 / t + 0.24039;
    const y = -0.9549476 * x * x * x - 1.37418593 * x * x + 2.09137015 * x - 0.16748867;
    return { x, y };
  }

  // CIE daylight locus.
  const x =
    t <= 7000
      ? 0.244063 + (0.09911e3 / t) + (2.9678e6 / (t * t)) - (4.607e9 / (t * t * t))
      : 0.23704 + (0.24748e3 / t) + (1.9018e6 / (t * t)) - (2.0064e9 / (t * t * t));
  const y = -3.0 * x * x + 2.87 * x - 0.275;
  return { x, y };
}

/**
 * Approximate CCT of a chromaticity, using McCamy's cubic. Good to a few Kelvin
 * near the locus, which is all the UI readout needs.
 */
export function chromaticityToTemperature(x: number, y: number): number {
  const n = (x - 0.3320) / (0.1858 - y);
  const cct = 449 * n * n * n + 3525 * n * n + 6823.3 * n + 5520.33;
  return Math.min(25000, Math.max(1667, cct));
}

/**
 * Signed distance from the Planckian/daylight locus, scaled to the ±150 tint
 * range photographers are used to. Positive is magenta, negative is green,
 * matching every mainstream raw converter.
 */
export function chromaticityToTint(x: number, y: number): number {
  const temperature = chromaticityToTemperature(x, y);
  const onLocus = temperatureToChromaticity(temperature);
  // v' axis in CIE 1960 UCS is where the green/magenta offset is meaningful.
  const uv = xyToUv(x, y);
  const locusUv = xyToUv(onLocus.x, onLocus.y);
  const duv = uv.v - locusUv.v;
  return clamp(-duv * 3000, -150, 150);
}

function xyToUv(x: number, y: number): { u: number; v: number } {
  const denominator = -2 * x + 12 * y + 3;
  return { u: (4 * x) / denominator, v: (6 * y) / denominator };
}

function uvToXy(u: number, v: number): { x: number; y: number } {
  const denominator = 2 * u - 8 * v + 4;
  return { x: (3 * u) / denominator, y: (2 * v) / denominator };
}

/** Chromaticity for a temperature plus a tint offset off the locus. */
export function whitePointFor(kelvin: number, tint: number): { x: number; y: number } {
  const locus = temperatureToChromaticity(kelvin);
  const uv = xyToUv(locus.x, locus.y);
  return uvToXy(uv.u, uv.v - tint / 3000);
}

/** XYZ of a chromaticity normalised to Y = 1. */
export function chromaticityToXyz(x: number, y: number): [number, number, number] {
  if (y <= 1e-6) return [0.9642, 1, 0.8249];
  return [x / y, 1, (1 - x - y) / y];
}

/**
 * Bradford chromatic adaptation matrix taking colours seen under `source`
 * to their appearance under `destination`, both given as XYZ white points.
 */
export function adaptationMatrix(
  source: [number, number, number],
  destination: [number, number, number],
): Matrix3 {
  const sourceCone = applyMatrix(BRADFORD, source);
  const destinationCone = applyMatrix(BRADFORD, destination);
  const scale: Matrix3 = [
    destinationCone[0] / sourceCone[0],
    0,
    0,
    0,
    destinationCone[1] / sourceCone[1],
    0,
    0,
    0,
    destinationCone[2] / sourceCone[2],
  ];
  return multiplyMatrix(BRADFORD_INVERSE, multiplyMatrix(scale, BRADFORD));
}

export const D65_XYZ: [number, number, number] = [0.95047, 1.0, 1.08883];

/**
 * The 3x3 matrix the shader applies to linear sRGB to re-balance the image as
 * if the scene light had been `kelvin`/`tint` rather than what the camera
 * recorded (`asShotKelvin`/`asShotTint`).
 *
 * Returns identity when the controls sit at the as-shot values, so "As Shot"
 * is bit-for-bit the decoder's own output.
 */
export function whiteBalanceMatrix(
  kelvin: number,
  tint: number,
  asShotKelvin: number,
  asShotTint: number,
): Matrix3 {
  if (
    Math.abs(kelvin - asShotKelvin) < 0.5 &&
    Math.abs(tint - asShotTint) < 0.05
  ) {
    return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  }

  const requested = whitePointFor(kelvin, tint);
  const asShot = whitePointFor(asShotKelvin, asShotTint);

  const requestedXyz = chromaticityToXyz(requested.x, requested.y);
  const asShotXyz = chromaticityToXyz(asShot.x, asShot.y);

  // The buffer is already balanced so that the as-shot illuminant reads as
  // D65 white. Re-balancing to `requested` means adapting D65 -> (D65 scaled by
  // the ratio of the two illuminants).
  const adapt = adaptationMatrix(requestedXyz, asShotXyz);
  const inXyz = multiplyMatrix(adapt, SRGB_TO_XYZ);
  const result = multiplyMatrix(XYZ_TO_SRGB, inXyz);

  // Preserve overall exposure: keep the luminance of white unchanged.
  const white = applyMatrix(result, [1, 1, 1]);
  const luminance = 0.2126 * white[0] + 0.7152 * white[1] + 0.0722 * white[2];
  if (luminance > 1e-6) {
    for (let i = 0; i < 9; i += 1) result[i] /= luminance;
  }
  return result;
}

/**
 * Estimates the as-shot temperature and tint from the camera's recorded
 * multipliers and its XYZ colour matrix, so the UI can display a real Kelvin
 * number instead of an arbitrary 5500 default.
 *
 * `camMul` are LibRaw's `cam_mul` (the multipliers that neutralise the scene
 * light) and `camXyz` is its 3x3 XYZ-to-camera matrix.
 */
export function estimateAsShotWhiteBalance(
  camMul: readonly number[] | undefined,
  camXyz: readonly (readonly number[])[] | undefined,
): { kelvin: number; tint: number; estimated: boolean } {
  const fallback = { kelvin: 5500, tint: 0, estimated: false };
  if (!camMul || camMul.length < 3) return fallback;
  const [r, g, b] = camMul;
  if (!(r > 0 && g > 0 && b > 0)) return fallback;

  // The illuminant in camera space is the reciprocal of the multipliers.
  const cameraIlluminant: [number, number, number] = [g / r, 1, g / b];

  let xyz: [number, number, number] | null = null;
  if (camXyz && camXyz.length >= 3 && camXyz[0] && camXyz[0].length >= 3) {
    const matrix: Matrix3 = [
      camXyz[0][0], camXyz[0][1], camXyz[0][2],
      camXyz[1][0], camXyz[1][1], camXyz[1][2],
      camXyz[2][0], camXyz[2][1], camXyz[2][2],
    ];
    const inverse = invertMatrix(matrix);
    if (inverse) xyz = applyMatrix(inverse, cameraIlluminant);
  }

  if (!xyz || !(xyz[0] + xyz[1] + xyz[2] > 1e-6) || xyz[1] <= 0) return fallback;

  const sum = xyz[0] + xyz[1] + xyz[2];
  const x = xyz[0] / sum;
  const y = xyz[1] / sum;
  const kelvin = chromaticityToTemperature(x, y);
  const tint = chromaticityToTint(x, y);
  if (!Number.isFinite(kelvin) || !Number.isFinite(tint)) return fallback;

  return {
    kelvin: Math.round(clamp(kelvin, 2000, 20000)),
    tint: Math.round(clamp(tint, -150, 150)),
    estimated: true,
  };
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}
